import { useMemo, useState } from "react";
import { Eye, EyeOff, Save, Star, Plus, Trash2, GripVertical } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Section } from "@/components/settings/Section";
import { buildMenuModel, GROUP_META } from "@/lib/menuLayout";

const FIXED = GROUP_META.filter((g) => g.id !== "custom"); // includes _top + infra..system

export function MenuCustomizer({ currentUser, setUser, open, onToggle }) {
  const { t } = useI18n();
  const restricted = currentUser?.department === "ksiegowosc";
  const admin = currentUser?.role === "admin";
  const model = useMemo(() => buildMenuModel(t, { restricted, admin }), [t, restricted, admin]);

  const [ml, setMl] = useState(() => {
    const l = currentUser?.menu_layout || {};
    let cg = l.custom_groups || [];
    if (!cg.length && l.custom_label) cg = [{ id: "custom", label: l.custom_label }];
    return { hidden: l.hidden || [], move: l.move || {}, order: l.order || {}, custom_groups: cg };
  });
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(null); // {gid, id}

  const customIds = new Set(ml.custom_groups.map((g) => g.id));
  const validGroup = (g) => g && (FIXED.some((f) => f.id === g) || customIds.has(g));
  const groupOf = (it) => (validGroup(ml.move[it.id]) ? ml.move[it.id] : it.dg);
  const isHidden = (id) => ml.hidden.includes(id);
  const toggleHidden = (id) => setMl((m) => ({ ...m, hidden: m.hidden.includes(id) ? m.hidden.filter((x) => x !== id) : [...m.hidden, id] }));
  const setGroup = (id, g) => setMl((m) => ({ ...m, move: { ...m.move, [id]: g } }));

  const groups = [...FIXED.map((g) => ({ id: g.id, label: g.id === "_top" ? t("Góra (luźne)") : t(g.label) })),
                  ...ml.custom_groups.map((g) => ({ id: g.id, label: g.label || t("Moje skróty"), custom: true }))];

  const kidsOf = (gid) => {
    const arr = model.filter((it) => groupOf(it) === gid);
    const o = ml.order[gid] || [];
    return arr.sort((a, b) => { const ia = o.indexOf(a.id), ib = o.indexOf(b.id); return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib); });
  };

  const onDrop = (gid, targetId) => {
    if (!drag) return;
    setMl((m) => {
      const next = { ...m, move: { ...m.move }, order: { ...m.order } };
      if (drag.gid !== gid) next.move[drag.id] = gid;
      const cur = kidsOf(gid).map((x) => x.id).filter((x) => x !== drag.id);
      const pos = targetId ? cur.indexOf(targetId) : cur.length;
      cur.splice(pos < 0 ? cur.length : pos, 0, drag.id);
      next.order[gid] = cur;
      return next;
    });
    setDrag(null);
  };

  const addGroup = () => setMl((m) => ({ ...m, custom_groups: [...m.custom_groups, { id: "c" + Date.now(), label: t("Nowa grupa") }] }));
  const renameGroup = (id, label) => setMl((m) => ({ ...m, custom_groups: m.custom_groups.map((g) => g.id === id ? { ...g, label } : g) }));
  const delGroup = (id) => setMl((m) => ({ ...m, custom_groups: m.custom_groups.filter((g) => g.id !== id),
    move: Object.fromEntries(Object.entries(m.move).filter(([, v]) => v !== id)) }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...ml, custom_label: "" };
      const { data } = await api.put("/auth/me/preferences", { menu_layout: payload });
      if (setUser) setUser((u) => ({ ...(u || {}), ...data }));
      toast.success(t("Menu zapisane"));
    } catch { toast.error(t("Nie udało się zapisać")); }
    finally { setBusy(false); }
  };
  const reset = () => setMl({ hidden: [], move: {}, order: {}, custom_groups: [] });

  const inp = "bg-slate-900 border border-slate-700 rounded px-1.5 py-1 text-[11px] outline-none focus:ring-1 focus:ring-cyan-500";

  return (
    <Section icon={Star} iconColor="text-fuchsia-400" title={t("Dostosuj menu (kolejność, grupy, pokaż/ukryj)")}
      testid="settings-menu-customizer" open={open} onToggle={onToggle}>
      <p className="text-xs text-slate-400 mb-3">{t("Przeciągaj pozycje (uchwyt), by ustawić kolejność. Ukrywaj, przenoś między grupami, twórz własne grupy. Dla menu bocznego. Zapis indywidualny.")}</p>

      <button type="button" onClick={addGroup} data-testid="menu-add-group" className="mb-3 bg-fuchsia-600 hover:bg-fuchsia-500 rounded px-2.5 py-1.5 text-sm flex items-center gap-1.5"><Plus size={14} /> {t("Dodaj własną grupę")}</button>

      <div className="space-y-3 max-h-[440px] overflow-y-auto pr-1">
        {groups.map((g) => {
          const kids = kidsOf(g.id);
          return (
            <div key={g.id} data-testid={`menu-cust-group-${g.id}`} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(g.id, null)}>
              <div className="flex items-center gap-2 mb-1">
                {g.custom ? (
                  <>
                    <Star size={12} className="text-fuchsia-400 shrink-0" />
                    <input value={g.label} onChange={(e) => renameGroup(g.id, e.target.value)} data-testid={`menu-group-label-${g.id}`} className={`${inp} flex-1`} />
                    <button type="button" onClick={() => delGroup(g.id)} data-testid={`menu-group-del-${g.id}`} className="text-red-300 hover:text-red-200"><Trash2 size={13} /></button>
                  </>
                ) : <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{g.label}</div>}
              </div>
              <div className="space-y-1 min-h-[6px]">
                {kids.map((it) => (
                  <div key={it.id} draggable onDragStart={() => setDrag({ gid: g.id, id: it.id })}
                    onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.stopPropagation(); onDrop(g.id, it.id); }}
                    className="flex items-center gap-2 bg-slate-900/50 rounded px-2 py-1 cursor-move" data-testid={`menu-cust-item-${it.id}`}>
                    <GripVertical size={13} className="text-slate-600 shrink-0" />
                    <button type="button" onClick={() => toggleHidden(it.id)} data-testid={`menu-cust-hide-${it.id}`}
                      className={isHidden(it.id) ? "text-slate-600" : "text-cyan-400"} title={isHidden(it.id) ? t("Pokaż") : t("Ukryj")}>
                      {isHidden(it.id) ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                    <span className={`text-xs flex-1 truncate ${isHidden(it.id) ? "text-slate-600 line-through" : "text-slate-200"}`}>{it.label}</span>
                    <select value={groupOf(it)} onChange={(e) => setGroup(it.id, e.target.value)} data-testid={`menu-cust-move-${it.id}`} className={inp}>
                      {groups.map((go) => <option key={go.id} value={go.id}>{go.label}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-2 mt-3">
        <button type="button" onClick={save} disabled={busy} data-testid="menu-cust-save"
          className="bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 rounded px-3 py-1.5 text-sm flex items-center gap-1.5"><Save size={14} /> {t("Zapisz menu")}</button>
        <button type="button" onClick={reset} data-testid="menu-cust-reset" className="text-xs text-slate-400 hover:text-slate-200 underline">{t("Przywróć domyślne")}</button>
      </div>
    </Section>
  );
}
